import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpgradeConfirmModal = ({ isOpen, onClose, plan, billingCycle }) => {
  if (!isOpen || !plan) return null;

  const currentPlanPrice = 2999;
  const daysRemaining = 22;
  const daysInMonth = 30;
  
  const proratedCredit = Math.round((currentPlanPrice / daysInMonth) * daysRemaining);
  const newPlanPrice = billingCycle === 'monthly' ? plan?.monthlyPrice : plan?.yearlyPrice;
  const amountDue = newPlanPrice - proratedCredit;

  const handleConfirmUpgrade = () => {
    console.log('Upgrading to:', plan?.name);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto elevation-xl animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="ArrowUp" size={20} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground font-heading">
              Confirm Plan Upgrade
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md hover:bg-muted transition-smooth flex items-center justify-center"
          >
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
          {/* Plan Details */}
          <div className="bg-gradient-to-br from-primary/10 to-accent/5 border border-border rounded-lg p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground font-caption mb-1">Upgrading to</p>
                <h4 className="text-lg lg:text-xl font-bold text-foreground font-heading">
                  {plan?.name} Plan
                </h4>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xl lg:text-2xl font-bold text-primary font-heading">
                  ₹{newPlanPrice?.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground font-caption">
                  /{billingCycle === 'monthly' ? 'month' : 'year'}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground font-caption">
              {plan?.description}
            </p>
          </div>

          {/* Proration Breakdown */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground font-body">
              Payment Breakdown
            </h4>
            <div className="space-y-2 p-3 lg:p-4 bg-muted/30 border border-border rounded-lg">
              <div className="flex items-start sm:items-center justify-between gap-2">
                <span className="text-xs lg:text-sm text-muted-foreground font-caption">
                  {plan?.name} Plan ({billingCycle})
                </span>
                <span className="text-xs lg:text-sm font-medium text-foreground font-body whitespace-nowrap">
                  ₹{newPlanPrice?.toLocaleString()}
                </span>
              </div>
              <div className="flex items-start sm:items-center justify-between gap-2">
                <span className="text-xs lg:text-sm text-muted-foreground font-caption">
                  Prorated credit ({daysRemaining} days remaining)
                </span>
                <span className="text-xs lg:text-sm font-medium text-success font-body whitespace-nowrap">
                  -₹{proratedCredit?.toLocaleString()}
                </span>
              </div>
              <div className="h-px bg-border my-2" />
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm lg:text-base font-semibold text-foreground font-body">
                  Amount Due Today
                </span>
                <span className="text-base lg:text-lg font-bold text-primary font-heading whitespace-nowrap">
                  ₹{amountDue?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground font-body">
              What You'll Get
            </h4>
            <div className="space-y-2">
              {plan?.features?.slice(0, 5)?.map((feature, index) => (
                (feature?.included || feature?.value) && (
                  <div key={index} className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-xs lg:text-sm text-foreground font-body break-words">
                      {feature?.name}
                      {feature?.value && (
                        <span className="ml-1 text-xs text-muted-foreground font-caption">
                          ({feature?.value})
                        </span>
                      )}
                    </span>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Important Notice */}
          <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground font-body mb-1">
                Billing Changes
              </p>
              <p className="text-xs text-muted-foreground font-caption">
                Your new plan will be active immediately. The next billing date will be {billingCycle === 'monthly' ? '30 days' : '1 year'} from today.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              size="default"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="default"
              fullWidth
              iconName="CheckCircle"
              iconPosition="left"
              onClick={handleConfirmUpgrade}
            >
              Confirm Upgrade
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeConfirmModal;